/**
 * Parent Link Service
 *
 * Handles parent-child relationship management including:
 * - Generating invite codes for athletes
 * - Linking parents to children
 * - Unlinking relationships
 * - Managing parent access to child data
 */

import {
  getDoc,
  getDocs,
  addDoc,
  updateDoc,
  deleteDoc,
  serverTimestamp,
  writeBatch,
  arrayUnion,
  arrayRemove
} from 'firebase/firestore';
import { db } from '../firebase';
import { clientKeyManager } from '../db/helpers/clientKeyManager';
import { ParentLink, ParentLinkStatus, UserProfile } from '../../../common/src/types/user';
import {
  ParentLinksRef_Collection,
  ParentLinkRef_Document,
  ParentLinksByParentRef_Query,
  ParentLinksByChildRef_Query,
  ParentLinkByInviteCodeRef_Query,
  UserRef_Document,
  TeamsRef_Collection
} from '../db';

// Helper function to safely convert Firestore Timestamp to Date
const toDate = (value: any): Date => {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  if (value.toDate && typeof value.toDate === 'function') return value.toDate();
  return new Date();
};

// Generate a unique 8-character invite code
const generateInviteCode = (): string => {
  const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZ23456789'; // Exclude confusing chars like 0/O, 1/I
  let code = '';
  for (let i = 0; i < 8; i++) {
    code += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return code;
};

export class ParentLinkService {

  // ============================================================================
  // INVITE CODE MANAGEMENT (For Athletes)
  // ============================================================================

  /**
   * Generate a new invite code for an athlete to share with their parent
   * The code expires in 7 days
   */
  static async generateParentInviteCode(athleteId: string): Promise<string> {
    const clientKey = await clientKeyManager.getClientKey();
    const userRef = UserRef_Document(athleteId, clientKey);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      throw new Error('User not found');
    }

    const userData = userDoc.data() as UserProfile;

    // Self-service families are one parent per child (the invite-code flow);
    // district families may carry several (linked_parent_ids, G8).
    if (userData.linked_parent_id || (userData.linked_parent_ids || []).length > 0) {
      throw new Error('You already have a linked parent. Unlink them first to invite a new parent.');
    }

    // Generate new code
    const inviteCode = generateInviteCode();
    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + 7); // 7 days expiration

    // Create a pending parent link record
    const linkData = {
      parent_id: '', // Will be filled when parent accepts
      child_id: athleteId,
      status: 'pending' as ParentLinkStatus,
      invite_code: inviteCode,
      child_name: userData.display_name || '',
      child_team_ids: userData.team_ids || [],
      created_at: serverTimestamp(),
    };

    await addDoc(ParentLinksRef_Collection(clientKey), linkData);

    // Update athlete's profile with the invite code
    await updateDoc(userRef, {
      parent_invite_code: inviteCode,
      parent_invite_code_expires_at: expiresAt,
      updated_at: serverTimestamp()
    });

    return inviteCode;
  }

  /**
   * Revoke/regenerate the current invite code
   */
  static async revokeParentInviteCode(athleteId: string): Promise<void> {
    const clientKey = await clientKeyManager.getClientKey();
    const userRef = UserRef_Document(athleteId, clientKey);

    // Delete any pending link records for this athlete
    const pendingLinks = await getDocs(
      ParentLinksByChildRef_Query(athleteId, 'pending', clientKey)
    );

    const batch = writeBatch(db);
    pendingLinks.docs.forEach(doc => {
      batch.delete(doc.ref);
    });

    // Clear the invite code from user profile
    batch.update(userRef, {
      parent_invite_code: null,
      parent_invite_code_expires_at: null,
      updated_at: serverTimestamp()
    });

    await batch.commit();
  }

  // ============================================================================
  // PARENT LINKING (For Parents)
  // ============================================================================

  /**
   * Accept an invite code and link parent to child
   */
  static async acceptParentInvite(parentId: string, inviteCode: string): Promise<ParentLink> {
    const clientKey = await clientKeyManager.getClientKey();

    // Find the pending link with this invite code
    const pendingLinksSnapshot = await getDocs(
      ParentLinkByInviteCodeRef_Query(inviteCode, clientKey)
    );

    if (pendingLinksSnapshot.empty) {
      throw new Error('Invalid or expired invite code');
    }

    const linkDoc = pendingLinksSnapshot.docs[0];
    const linkData = linkDoc.data();

    // Verify the link is still pending
    if (linkData.status !== 'pending') {
      throw new Error('This invite code has already been used');
    }

    // Get parent's profile
    const parentRef = UserRef_Document(parentId, clientKey);
    const parentDoc = await getDoc(parentRef);

    if (!parentDoc.exists()) {
      throw new Error('Parent user not found');
    }

    const parentData = parentDoc.data() as UserProfile;

    // Get child's profile to validate
    const childRef = UserRef_Document(linkData.child_id, clientKey);
    const childDoc = await getDoc(childRef);

    if (!childDoc.exists()) {
      throw new Error('Child user not found');
    }

    const childData = childDoc.data() as UserProfile;

    // Verify child doesn't already have a parent
    if (childData.linked_parent_id || (childData.linked_parent_ids || []).length > 0) {
      throw new Error('This athlete already has a linked parent');
    }

    const batch = writeBatch(db);

    // Update the link record
    batch.update(linkDoc.ref, {
      parent_id: parentId,
      parent_email: parentData.email,
      parent_name: parentData.display_name,
      status: 'active',
      linked_at: serverTimestamp(),
    });

    // Update parent's profile
    batch.update(parentRef, {
      linked_children_ids: arrayUnion(linkData.child_id),
      updated_at: serverTimestamp()
    });

    // Update child's profile (array is the model; the scalar rides along one release)
    batch.update(childRef, {
      linked_parent_ids: arrayUnion(parentId),
      linked_parent_id: parentId,
      parent_invite_code: null,
      parent_invite_code_expires_at: null,
      updated_at: serverTimestamp()
    });

    // Add parent to any teams the child is on
    if (childData.team_ids && childData.team_ids.length > 0) {
      for (const teamId of childData.team_ids) {
        try {
          const teamRef = TeamsRef_Collection(clientKey);
          // Note: We need to use a different approach since we're using batch
          // We'll update teams after the batch
        } catch (e) {        }
      }
    }

    await batch.commit();

    // Update teams separately (add parent_id to team's parent_ids array)
    if (childData.team_ids && childData.team_ids.length > 0) {
      for (const teamId of childData.team_ids) {
        try {
          const { TeamRef_Document } = await import('../db');
          const teamRef = TeamRef_Document(teamId, clientKey);
          await updateDoc(teamRef, {
            parent_ids: arrayUnion(parentId),
            updated_at: serverTimestamp()
          });
        } catch (e) {        }
      }
    }

    return {
      id: linkDoc.id,
      parent_id: parentId,
      child_id: linkData.child_id,
      status: 'active',
      invite_code: inviteCode,
      parent_email: parentData.email || undefined,
      parent_name: parentData.display_name || undefined,
      child_name: linkData.child_name,
      child_team_ids: linkData.child_team_ids,
      created_at: toDate(linkData.created_at),
      linked_at: new Date(),
    };
  }

  // ============================================================================
  // UNLINKING
  // ============================================================================

  /**
   * Unlink a parent from a child
   * Can be called by either the parent, child, or an admin
   */
  static async unlinkParent(
    childId: string,
    revokedBy: string // userId of who is revoking
  ): Promise<void> {
    const clientKey = await clientKeyManager.getClientKey();

    // Find the active link
    const activeLinksSnapshot = await getDocs(
      ParentLinksByChildRef_Query(childId, 'active', clientKey)
    );

    if (activeLinksSnapshot.empty) {
      throw new Error('No active parent link found');
    }

    const linkDoc = activeLinksSnapshot.docs[0];
    const linkData = linkDoc.data();
    const parentId = linkData.parent_id;

    const batch = writeBatch(db);

    // Update the link record to revoked
    batch.update(linkDoc.ref, {
      status: 'revoked',
      revoked_at: serverTimestamp(),
      revoked_by: revokedBy
    });

    // Update parent's profile
    const parentRef = UserRef_Document(parentId, clientKey);
    batch.update(parentRef, {
      linked_children_ids: arrayRemove(childId),
      updated_at: serverTimestamp()
    });

    // Update child's profile: drop this parent from the array; the legacy
    // scalar clears only if it named this parent.
    const childRef = UserRef_Document(childId, clientKey);
    const childSnap = await getDoc(childRef);
    const scalarWasThisParent = childSnap.exists() && childSnap.data()?.linked_parent_id === parentId;
    batch.update(childRef, {
      linked_parent_ids: arrayRemove(parentId),
      ...(scalarWasThisParent ? { linked_parent_id: null } : {}),
      updated_at: serverTimestamp()
    });

    await batch.commit();

    // Remove parent from teams
    if (linkData.child_team_ids && linkData.child_team_ids.length > 0) {
      for (const teamId of linkData.child_team_ids) {
        try {
          const { TeamRef_Document } = await import('../db');
          const teamRef = TeamRef_Document(teamId, clientKey);
          await updateDoc(teamRef, {
            parent_ids: arrayRemove(parentId),
            updated_at: serverTimestamp()
          });
        } catch (e) {        }
      }
    }
  }

  // ============================================================================
  // QUERY METHODS
  // ============================================================================

  /**
   * Get all children linked to a parent
   */
  static async getLinkedChildren(parentId: string): Promise<ParentLink[]> {
    const clientKey = await clientKeyManager.getClientKey();
    const snapshot = await getDocs(
      ParentLinksByParentRef_Query(parentId, 'active', clientKey)
    );

    return snapshot.docs.map(doc => {
      const data = doc.data();
      return {
        id: doc.id,
        parent_id: data.parent_id,
        child_id: data.child_id,
        status: data.status,
        invite_code: data.invite_code,
        parent_email: data.parent_email,
        parent_name: data.parent_name,
        child_name: data.child_name,
        child_team_ids: data.child_team_ids,
        created_at: toDate(data.created_at),
        linked_at: toDate(data.linked_at),
        notification_preferences: data.notification_preferences
      } as ParentLink;
    });
  }

  /**
   * Get the parent linked to a child
   */
  static async getLinkedParent(childId: string): Promise<ParentLink | null> {
    const clientKey = await clientKeyManager.getClientKey();
    const snapshot = await getDocs(
      ParentLinksByChildRef_Query(childId, 'active', clientKey)
    );

    if (snapshot.empty) {
      return null;
    }

    const doc = snapshot.docs[0];
    const data = doc.data();

    return {
      id: doc.id,
      parent_id: data.parent_id,
      child_id: data.child_id,
      status: data.status,
      invite_code: data.invite_code,
      parent_email: data.parent_email,
      parent_name: data.parent_name,
      child_name: data.child_name,
      child_team_ids: data.child_team_ids,
      created_at: toDate(data.created_at),
      linked_at: toDate(data.linked_at),
      notification_preferences: data.notification_preferences
    } as ParentLink;
  }

  /**
   * Check if a user is the parent of another user
   */
  static async isParentOf(parentId: string, childId: string): Promise<boolean> {
    const clientKey = await clientKeyManager.getClientKey();
    const childRef = UserRef_Document(childId, clientKey);
    const childDoc = await getDoc(childRef);

    if (!childDoc.exists()) {
      return false;
    }

    const childData = childDoc.data() as UserProfile;
    return (
      (childData.linked_parent_ids || []).includes(parentId) ||
      childData.linked_parent_id === parentId
    );
  }

  /**
   * Update notification preferences for a parent link
   */
  static async updateNotificationPreferences(
    linkId: string,
    preferences: ParentLink['notification_preferences']
  ): Promise<void> {
    const clientKey = await clientKeyManager.getClientKey();
    const linkRef = ParentLinkRef_Document(linkId, clientKey);

    await updateDoc(linkRef, {
      notification_preferences: preferences
    });
  }

  /**
   * Get pending invite code for an athlete
   */
  static async getPendingInviteCode(athleteId: string): Promise<string | null> {
    const clientKey = await clientKeyManager.getClientKey();
    const userRef = UserRef_Document(athleteId, clientKey);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      return null;
    }

    const userData = userDoc.data() as UserProfile;

    // Check if code is expired
    if (userData.parent_invite_code && userData.parent_invite_code_expires_at) {
      const expiresAt = toDate(userData.parent_invite_code_expires_at);
      if (expiresAt > new Date()) {
        return userData.parent_invite_code;
      }
      // Code expired, clear it
      await updateDoc(userRef, {
        parent_invite_code: null,
        parent_invite_code_expires_at: null,
        updated_at: serverTimestamp()
      });
    }

    return null;
  }
}
